// =============================================================================
// EditBookingModal — operator edits a confirmed booking
// =============================================================================
// Two tabs:
//   1. Klantgegevens — name / phone / locale / email (action='customer')
//   2. Datum & slot — single-leg slot + party_size (action='slots',
//                     force-override for capacity)
//
// v1 limitations:
//   - Slot tab only supports single-leg edits. Split bookings should still
//     be reschedulable via the customer manage portal (which handles split-2
//     and split-3) — the admin sees a hint when the booking is split.
//   - Editing party size or slot does NOT auto-adjust total_cents. Operator
//     handles refund/charge delta via cancel-refund or walk-in.
//   - Email can be changed; UNIQUE collision is reported (409 'email_already_in_use').
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { todayVenue } from '../lib/dates';
import { formatError } from '../lib/errors';
import { fetchSlotsForDay } from '../lib/walkin-data';
import type { Departure } from '../lib/departures';
import {
  editBooking,
  type EditReduceRequest,
  type EditReduceResponse,
} from '../api/edit-booking';
import {
  resendBookingEmail,
  type ResendEmailAction,
} from '../api/resend-email';
import type { BookingRow, BookingSlotRow, ExperiencePickRow } from '../types';
import { useSingleRoomLocations, useRoomToLocation } from '../lib/room-counts';
import { proratedPriceCents, reductionCreditCents } from '../lib/refund-math';
import { supabase } from '../lib/supabase';

type Tab = 'customer' | 'slot' | 'reduce';

interface Props {
  booking: BookingRow;
  onClose: (changed: boolean) => void;
}

const LOCALES: Array<{ code: string; label: string }> = [
  { code: 'nl', label: 'NL' },
  { code: 'en', label: 'EN' },
];

export function EditBookingModal({ booking, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('customer');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // After a successful save we don't auto-dismiss any more — we ask whether to
  // resend the confirmation email. `savedAction` records what the operator
  // just changed so the prompt can phrase itself appropriately and so we
  // don't show the form again.
  const [savedAction, setSavedAction] = useState<Tab | null>(null);
  const [resendBusy, setResendBusy] = useState<ResendEmailAction | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendAndClose = useCallback(
    async (action: ResendEmailAction) => {
      setResendBusy(action);
      setResendError(null);
      const result = await resendBookingEmail({
        booking_id: booking.id,
        action,
      });
      setResendBusy(null);
      if (!result.ok) {
        setResendError(
          result.error?.message ??
            result.error?.error ??
            `HTTP ${result.status}`,
        );
        return;
      }
      onClose(true);
    },
    [booking.id, onClose],
  );

  // ---- Customer tab state --------------------------------------------------
  const initialCustomer = booking.customers ?? null;
  // Customer-less balie walk-in: the only name is the booking's own
  // guest_name; the "Klantgegevens" tab becomes a single "Naam" field saved
  // via fn_admin_edit_guest_name, and there is no e-mail to resend to.
  const hasCustomer = initialCustomer != null;
  const initialGuestName = booking.guest_name ?? '';
  const [guestName, setGuestName] = useState(initialGuestName);
  const [firstName, setFirstName] = useState(initialCustomer?.first_name ?? '');
  const [lastName, setLastName] = useState(initialCustomer?.last_name ?? '');
  const [email, setEmail] = useState(initialCustomer?.email ?? '');
  const [phone, setPhone] = useState(initialCustomer?.phone ?? '');
  const [locale, setLocale] = useState(booking.locale ?? 'nl');
  const [customerReason, setCustomerReason] = useState('');

  // ---- Slot tab state ------------------------------------------------------
  const firstSlot = booking.booking_slots[0];
  // A departure with 2+ experience-picks (e.g. Egypt + Odyssey on one slot).
  // Editing these isn't a single-leg slot rewrite — it's a time-only MOVE that
  // shifts every pick together. Distinct from a genuine time-split (one pick
  // spread over multiple slots), which is what `isTimeSplit` flags.
  const isMultiPick = booking.experience_picks.length > 1;
  const isTimeSplit = !isMultiPick && booking.booking_slots.length > 1;

  const [partySize, setPartySize] = useState<number>(booking.party_size);
  const [date, setDate] = useState<string>(() =>
    firstSlot
      ? new Date(firstSlot.slot_start).toLocaleDateString('en-CA', {
          timeZone: 'Europe/Amsterdam',
        })
      : todayVenue(),
  );
  const [slots, setSlots] = useState<Departure[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [pickedSlot, setPickedSlot] = useState<Departure | null>(null);
  // 'force' = single leg with force=true, 'split' = two legs into the next slot.
  // 'force' is the default fallback; UI selects 'split' when split-2 is feasible.
  const [overCapacityChoice, setOverCapacityChoice] = useState<'force' | 'split'>('force');
  const [slotReason, setSlotReason] = useState('');
  const singleRoomLocations = useSingleRoomLocations();
  const roomToLocation = useRoomToLocation();

  // First load only: did we auto-pick the existing slot yet? Once we do,
  // changing date/experience clears it again so the next user pick is clean.
  const [autoPicked, setAutoPicked] = useState(false);

  // ---- Reduce (Verkleinen) tab state --------------------------------------
  // Single-slot path: new TOTAL party for the one slot.
  const [reduceNewParty, setReduceNewParty] = useState<number>(
    Math.max(1, booking.party_size - 1),
  );
  // Per-slot path (time-split / multi-pick): which booking_slot to shrink and
  // its new size. Default to the first slot, one seat fewer.
  const slotPickCount = useCallback(
    (depExpId?: string): number =>
      booking.booking_slots.filter(
        (s) => s.departure_experience_id === depExpId,
      ).length,
    [booking.booking_slots],
  );
  const [reduceSlotId, setReduceSlotId] = useState<string | null>(
    booking.booking_slots.length > 1
      ? (booking.booking_slots[0]?.id ?? null)
      : null,
  );
  const [reduceSlotNewParty, setReduceSlotNewParty] = useState<number>(() => {
    const s = booking.booking_slots.length > 1 ? booking.booking_slots[0] : null;
    if (!s) return 1;
    const min =
      booking.booking_slots.filter(
        (x) => x.departure_experience_id === s.departure_experience_id,
      ).length >= 2
        ? 0
        : 1;
    return Math.max(min, s.party_size - 1);
  });
  const [reduceReason, setReduceReason] = useState('');
  const [reduceVoucher, setReduceVoucher] = useState<
    EditReduceResponse['voucher'] | null
  >(null);
  const [voucherCopied, setVoucherCopied] = useState(false);

  const experienceId = booking.experiences?.id ?? null;
  // Restrict the slot picker to the booking's existing location. Resolved
  // via the room-counts topology cache; null while loading (we'll fall back
  // to the unfiltered query and narrow client-side).
  const bookingLocationId = firstSlot
    ? roomToLocation(firstSlot.room_id)
    : null;
  // Used by the SlotForm to render a "Huidig" badge on whichever slot row
  // matches the booking's existing first slot — so even after the operator
  // clicks elsewhere they can always see where they started.
  const existingSlotKey = firstSlot
    ? `${firstSlot.room_id}|${firstSlot.slot_start}`
    : null;

  // ---- Load slots when (date, experienceId, locationId) changes ----------
  useEffect(() => {
    if (tab !== 'slot' || !experienceId || !date) {
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    setSlotsError(null);
    // Only clear pickedSlot when the operator explicitly changes the date.
    // The first load on tab open should preserve the auto-picked existing slot.
    if (autoPicked) setPickedSlot(null);
    (async () => {
      try {
        const rows = await fetchSlotsForDay(date, experienceId, bookingLocationId);
        if (cancelled) return;
        // Belt-and-suspenders: narrow to the booking's location even if the
        // server returned more (e.g. when bookingLocationId was still null
        // during the first fetch). Multi-pick moves keep the same room, so
        // also pin the list to the booking's current room — the operator only
        // changes the time, not the room (which serves both experiences).
        const filtered = rows.filter(
          (r) =>
            (!bookingLocationId || r.location_id === bookingLocationId) &&
            (!isMultiPick || !firstSlot || r.room_id === firstSlot.room_id),
        );
        filtered.sort((a, b) => a.slot_start.localeCompare(b.slot_start));
        setSlots(filtered);

        // First-time auto-pick: find the booking's existing first slot in the
        // freshly loaded list and select it so the operator only has to change
        // party size. Subsequent loads (operator switched date) skip this.
        if (!autoPicked && firstSlot) {
          const match = filtered.find(
            (d) =>
              d.room_id    === firstSlot.room_id &&
              d.slot_start === firstSlot.slot_start,
          );
          if (match) setPickedSlot(match);
          setAutoPicked(true);
        }
      } catch (e) {
        if (!cancelled) setSlotsError(formatError(e));
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, experienceId, date, bookingLocationId]);

  // ---- Close on Escape ---------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  // ---- Derived flags -----------------------------------------------------
  // The booking's own existing party at a slot doesn't count against itself
  // when re-saving — fn_admin_edit_booking_slots filters it out. Mirror that
  // here so the UI doesn't false-positive when the operator just bumps the
  // party size on the SAME slot.
  const ownPartyAt = useCallback((slotStart: string): number => {
    return booking.booking_slots
      .filter((s) => s.slot_start === slotStart)
      .reduce((n, s) => n + s.party_size, 0);
  }, [booking.booking_slots]);

  const effectiveRemainingFor = useCallback((slot: Departure): number => {
    return Math.max(0, slot.capacity - slot.booked + ownPartyAt(slot.slot_start));
  }, [ownPartyAt]);

  const remainingCapacity = pickedSlot ? effectiveRemainingFor(pickedSlot) : 0;
  const isOverCapacity = pickedSlot != null && partySize > remainingCapacity;

  // Split-2 candidate: next slot in the same room with strictly later
  // slot_start. We pick the chronologically next one (regardless of
  // experience? — same room narrows enough; the loaded list is already
  // filtered by experience via fetchSlotsForDay).
  const splitNext = useMemo<Departure | null>(() => {
    if (!pickedSlot) return null;
    const candidates = slots
      .filter(
        (d) =>
          d.room_id === pickedSlot.room_id &&
          d.slot_start > pickedSlot.slot_start,
      )
      .sort((a, b) => a.slot_start.localeCompare(b.slot_start));
    return candidates[0] ?? null;
  }, [pickedSlot, slots]);

  const overflow = isOverCapacity ? partySize - remainingCapacity : 0;
  const splitNextRemaining = splitNext ? effectiveRemainingFor(splitNext) : 0;
  // Multi-pick departures move as a unit — no split-to-next-slot option; the
  // only over-capacity escape is force.
  const canSplit = !isMultiPick && isOverCapacity && splitNext != null && splitNextRemaining >= overflow;

  // Default the over-capacity choice to 'split' when feasible, 'force' otherwise.
  useEffect(() => {
    if (isOverCapacity) {
      setOverCapacityChoice(canSplit ? 'split' : 'force');
    }
  }, [isOverCapacity, canSplit]);

  const customerDirty = hasCustomer
    ? (
        firstName !== (initialCustomer?.first_name ?? '') ||
        lastName !== (initialCustomer?.last_name ?? '') ||
        email !== (initialCustomer?.email ?? '') ||
        phone !== (initialCustomer?.phone ?? '') ||
        locale !== (booking.locale ?? 'nl')
      )
    : guestName.trim() !== initialGuestName.trim();

  const customerValid = hasCustomer
    ? (
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      )
    : guestName.trim().length > 0;

  const slotValid =
    pickedSlot != null && partySize >= 1 && partySize <= 30 &&
    (
      !isOverCapacity ||
      overCapacityChoice === 'force' ||
      (overCapacityChoice === 'split' && canSplit)
    );

  // ---- Submit handlers ---------------------------------------------------
  const submitCustomer = useCallback(async () => {
    if (!customerValid || !customerDirty || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (!hasCustomer) {
        // Anonymous booking: rename the booking's own name snapshot only.
        const result = await editBooking({
          action: 'guest_name',
          booking_id: booking.parent_booking_id,
          guest_name: guestName.trim(),
          reason: customerReason.trim().length > 0 ? customerReason.trim() : null,
        });
        if (!result.ok) {
          const e = result.error;
          setSubmitError(e?.message ?? e?.error ?? `HTTP ${result.status}`);
          return;
        }
        setSavedAction('customer');
        return;
      }

      // BookingRow.customers omits customer_id; fetch it from the parent booking.
      // NB: booking.id is the DEPARTURE id post multi-experience cart — the
      // customer lives on the parent booking, so key on parent_booking_id.
      // Using booking.id here returns 0 rows (PGRST116).
      const { data, error } = await supabase
        .schema('booking')
        .from('bookings')
        .select('customer_id')
        .eq('id', booking.parent_booking_id)
        .single();
      if (error || !data?.customer_id) {
        throw error ?? new Error('customer_id_missing');
      }
      const result = await editBooking({
        action: 'customer',
        customer_id: data.customer_id as string,
        // Follow-through: this booking's own name snapshot tracks the
        // deliberate correction (other bookings of the customer keep theirs).
        booking_id: booking.parent_booking_id,
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        email:      email.trim(),
        phone:      phone.trim().length > 0 ? phone.trim() : '',
        locale,
        reason: customerReason.trim().length > 0 ? customerReason.trim() : null,
      });
      if (!result.ok) {
        const e = result.error;
        const msg = e?.message ?? e?.error ?? `HTTP ${result.status}`;
        setSubmitError(msg);
        return;
      }
      setSavedAction('customer');
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [
    hasCustomer, customerValid, customerDirty, submitting,
    booking.parent_booking_id, guestName, firstName, lastName, email, phone, locale, customerReason,
  ]);

  const submitSlot = useCallback(async () => {
    if (!slotValid || !pickedSlot || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Multi-pick: a time-only move. Every pick shifts to the picked slot,
      // keeping its experience / room / party size. No legs, no party change.
      if (isMultiPick) {
        const result = await editBooking({
          action: 'move',
          booking_id: booking.id,
          new_slot_start: pickedSlot.slot_start,
          // The only over-capacity escape for a multi-pick move is force.
          force: isOverCapacity,
          force_arrived: booking.lifecycle_status === 'arrived',
          reason: slotReason.trim().length > 0 ? slotReason.trim() : null,
        });
        if (!result.ok) {
          const e = result.error;
          setSubmitError(e?.message ?? e?.error ?? `HTTP ${result.status}`);
          return;
        }
        setSavedAction('slot');
        return;
      }

      // Build the legs.
      // - Not over capacity OR operator chose force: one leg, full party_size.
      // - Operator chose split-2: first leg fills picked slot's effective
      //   remaining; overflow goes to the next slot.
      const useSplit = isOverCapacity && overCapacityChoice === 'split' && canSplit && splitNext;
      const legs = useSplit
        ? [
            {
              room_id: pickedSlot.room_id,
              slot_start: pickedSlot.slot_start,
              party_size: remainingCapacity,
            },
            {
              room_id: splitNext.room_id,
              slot_start: splitNext.slot_start,
              party_size: partySize - remainingCapacity,
            },
          ]
        : [
            {
              room_id: pickedSlot.room_id,
              slot_start: pickedSlot.slot_start,
              party_size: partySize,
            },
          ];

      const result = await editBooking({
        action: 'slots',
        booking_id: booking.id,
        new_party_size: partySize,
        new_slots: legs,
        // force only needed for the "over capacity" path. Split keeps each
        // leg under capacity so force is not required.
        force: isOverCapacity && overCapacityChoice === 'force',
        // force_arrived bypasses the partially_arrived check on the SQL side
        // when the operator is moving an already-checked-in booking.
        force_arrived: booking.lifecycle_status === 'arrived',
        reason: slotReason.trim().length > 0 ? slotReason.trim() : null,
      });
      if (!result.ok) {
        const e = result.error;
        const msg = e?.message ?? e?.error ?? `HTTP ${result.status}`;
        setSubmitError(msg);
        return;
      }
      setSavedAction('slot');
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [
    slotValid, pickedSlot, submitting, booking.id, partySize, isMultiPick,
    isOverCapacity, overCapacityChoice, canSplit, splitNext, remainingCapacity,
    slotReason, booking.lifecycle_status,
  ]);

  // ---- Reduce (Verkleinen) derived + submit -------------------------------
  // Reduce-with-tegoedbon needs a paid booking with 2+ people that is still
  // confirmed/arrived. A single-slot departure reduces its whole party; a
  // time-split / multi-pick departure reduces ONE chosen timeslot (slot path).
  const isPaid = booking.payment_status === 'paid';
  const isSingleSlot = booking.booking_slots.length === 1;
  const reduceEligible =
    isPaid &&
    booking.party_size >= 2 &&
    (booking.lifecycle_status === 'confirmed' ||
      booking.lifecycle_status === 'arrived');

  // --- Single-slot path -----------------------------------------------------
  const reduceTotalCents = booking.total_cents ?? 0;
  // Mirror the RPC: new price = round(total × new/old); credit = total − new.
  const reduceNewTotalCents = proratedPriceCents(
    reduceTotalCents,
    booking.party_size,
    reduceNewParty,
  );
  const reduceCreditCents = reductionCreditCents(
    reduceTotalCents,
    booking.party_size,
    reduceNewParty,
  );

  const reduceValid =
    reduceEligible &&
    isSingleSlot &&
    Number.isInteger(reduceNewParty) &&
    reduceNewParty >= 1 &&
    reduceNewParty < booking.party_size;

  // --- Per-slot path (time-split / multi-pick) ------------------------------
  // The selected slot pins the pick whose price prorates; 0 is only allowed when
  // that pick has 2+ legs (drops a leg, RPC enforces this too).
  const reduceSelectedSlot =
    booking.booking_slots.find((s) => s.id === reduceSlotId) ?? null;
  const reduceSelectedPick = reduceSelectedSlot
    ? booking.experience_picks.find(
        (p) => p.id === reduceSelectedSlot.departure_experience_id,
      ) ?? null
    : null;
  const reduceSlotMin =
    reduceSelectedSlot &&
    slotPickCount(reduceSelectedSlot.departure_experience_id) >= 2
      ? 0
      : 1;
  const reduceSlotRemoved = reduceSelectedSlot
    ? reduceSelectedSlot.party_size - reduceSlotNewParty
    : 0;
  const reducePickTotalCents = reduceSelectedPick?.price_cents ?? 0;
  const reducePickParty = reduceSelectedPick?.party_size ?? 0;
  const reduceSlotNewPickParty = reducePickParty - reduceSlotRemoved;
  const reduceSlotCreditCents = reductionCreditCents(
    reducePickTotalCents,
    reducePickParty,
    reduceSlotNewPickParty,
  );
  const reduceSlotNewBookingTotalCents =
    (booking.total_cents ?? 0) - reduceSlotCreditCents;

  const reduceSlotValid =
    reduceEligible &&
    !isSingleSlot &&
    reduceSelectedSlot != null &&
    Number.isInteger(reduceSlotNewParty) &&
    reduceSlotNewParty >= reduceSlotMin &&
    reduceSlotNewParty < reduceSelectedSlot.party_size &&
    reduceSlotNewPickParty >= 1;

  // Selecting a slot resets its new-count to one fewer (respecting the min).
  const selectReduceSlot = useCallback(
    (slotId: string) => {
      setReduceSlotId(slotId);
      const slot = booking.booking_slots.find((s) => s.id === slotId);
      if (!slot) return;
      const min =
        slotPickCount(slot.departure_experience_id) >= 2 ? 0 : 1;
      setReduceSlotNewParty(Math.max(min, slot.party_size - 1));
    },
    [booking.booking_slots, slotPickCount],
  );

  const reduceCanSubmit = isSingleSlot ? reduceValid : reduceSlotValid;

  const submitReduce = useCallback(async () => {
    if (!reduceCanSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const reasonStr =
        reduceReason.trim().length > 0 ? reduceReason.trim() : null;
      const forceArrived = booking.lifecycle_status === 'arrived';
      const payload: EditReduceRequest = isSingleSlot
        ? {
            action: 'reduce',
            booking_id: booking.id,
            new_party_size: reduceNewParty,
            force_arrived: forceArrived,
            reason: reasonStr,
          }
        : {
            action: 'reduce',
            booking_id: booking.id,
            slot_id: reduceSlotId ?? undefined,
            new_slot_party_size: reduceSlotNewParty,
            force_arrived: forceArrived,
            reason: reasonStr,
          };
      const result = await editBooking<EditReduceResponse>(payload);
      if (!result.ok || !result.data) {
        const e = result.error;
        setSubmitError(e?.message ?? e?.error ?? `HTTP ${result.status}`);
        return;
      }
      setReduceVoucher(result.data.voucher);
      setSavedAction('reduce');
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [
    reduceCanSubmit, submitting, booking.id, booking.lifecycle_status,
    isSingleSlot, reduceNewParty, reduceSlotId, reduceSlotNewParty, reduceReason,
  ]);

  const copyReduceVoucher = useCallback(async () => {
    if (!reduceVoucher) return;
    try {
      await navigator.clipboard.writeText(reduceVoucher.code);
      setVoucherCopied(true);
      setTimeout(() => setVoucherCopied(false), 1500);
    } catch {
      /* clipboard API unavailable — operator can select the code manually */
    }
  }, [reduceVoucher]);

  // ---- Render -------------------------------------------------------------
  return (
    <div className="dep-modal-backdrop" onClick={() => { /* close via ✕ or Esc only — don't lose form data on a stray click */ }}>
      <div className="dep-modal edit-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div>
            <h2>Boeking bewerken</h2>
            <div className="dep-modal-sub muted">{booking.booking_number}</div>
          </div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => !submitting && onClose(false)}
            aria-label="Sluiten"
          >
            ✕
          </button>
        </header>

        {savedAction === null ? (
        <div className="edit-tabs">
          <button
            className={`edit-tab ${tab === 'customer' ? 'edit-tab-active' : ''}`}
            onClick={() => setTab('customer')}
            disabled={submitting}
          >
            {hasCustomer ? 'Klantgegevens' : 'Naam'}
          </button>
          <button
            className={`edit-tab ${tab === 'slot' ? 'edit-tab-active' : ''}`}
            onClick={() => setTab('slot')}
            disabled={submitting}
          >
            Datum &amp; slot
          </button>
          {isPaid ? (
            <button
              className={`edit-tab ${tab === 'reduce' ? 'edit-tab-active' : ''}`}
              onClick={() => setTab('reduce')}
              disabled={submitting}
            >
              Verkleinen
            </button>
          ) : null}
        </div>
        ) : null}

        <div className="dep-modal-body">
          {savedAction !== null ? (
            <div className="walkin-step">
              {savedAction === 'reduce' ? (
                <>
                  <div className="walkin-success-title">✓ Aantal verkleind</div>
                  {reduceVoucher ? (
                    <>
                      <p className="muted" style={{ marginTop: '0.4rem' }}>
                        {reduceVoucher.email_sent
                          ? 'Er is een tegoedbon naar de klant gemaild.'
                          : 'Let op: de e-mail kon niet worden verstuurd. Geef de code hieronder zelf aan de klant door.'}
                      </p>
                      <div className="cancel-voucher-result">
                        <strong className="voucher-code">{reduceVoucher.code}</strong>
                        <span className="muted">
                          {' · € '}{(reduceVoucher.amount_cents / 100).toFixed(2)}
                        </span>
                      </div>
                      <button
                        className="btn-link"
                        onClick={() => void copyReduceVoucher()}
                      >
                        {voucherCopied ? 'Gekopieerd ✓' : 'Kopieer code'}
                      </button>
                    </>
                  ) : (
                    <p className="muted" style={{ marginTop: '0.4rem' }}>
                      De boeking is verkleind.
                    </p>
                  )}
                  <p className="muted" style={{ marginTop: '0.6rem' }}>
                    Wil je de bijgewerkte bevestiging naar de klant sturen?
                  </p>
                </>
              ) : (
                <>
                  <h3 className="neoseum-section-title">Wijzigingen opgeslagen</h3>
                  <p className="muted" style={{ marginTop: '0.4rem' }}>
                    {savedAction === 'slot'
                      ? 'De slot- of party-size-wijziging is doorgevoerd.'
                      : hasCustomer
                        ? 'De klantgegevens zijn bijgewerkt.'
                        : 'De naam is bijgewerkt.'}
                    {hasCustomer
                      ? ' Wil je de bevestigingsmail naar de klant sturen?'
                      : ' Deze boeking heeft geen e-mailadres (balie), er is niets te versturen.'}
                  </p>
                </>
              )}
              {resendError ? (
                <div className="error-card walkin-submit-error">{resendError}</div>
              ) : null}
              <div className="walkin-step-actions">
                <button
                  className="btn-link"
                  disabled={resendBusy !== null}
                  onClick={() => onClose(true)}
                >
                  {hasCustomer ? 'Sluiten zonder versturen' : 'Sluiten'}
                </button>
                {hasCustomer ? (
                  <button
                    className="walkin-primary-btn"
                    disabled={resendBusy !== null}
                    onClick={() => void handleResendAndClose('confirmation')}
                  >
                    {resendBusy === 'confirmation' ? 'Versturen…' : 'Bevestiging versturen'}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
          <>
          {tab === 'customer' && !hasCustomer && (
            <div className="walkin-step">
              <div className="cancel-warn">
                Balie-boeking zonder e-mailadres: alleen de naam op deze boeking
                kan worden aangepast. Er is geen klantrecord en er worden geen
                mails verstuurd.
              </div>
              <label className="walkin-field">
                <span>Naam</span>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  autoFocus
                />
              </label>
              <label className="walkin-field">
                <span>Reden (optioneel, wordt gelogd)</span>
                <textarea
                  rows={2}
                  value={customerReason}
                  onChange={(e) => setCustomerReason(e.target.value)}
                />
              </label>
            </div>
          )}

          {tab === 'customer' && hasCustomer && (
            <CustomerForm
              firstName={firstName}
              lastName={lastName}
              email={email}
              phone={phone}
              locale={locale}
              reason={customerReason}
              onFirstName={setFirstName}
              onLastName={setLastName}
              onEmail={setEmail}
              onPhone={setPhone}
              onLocale={setLocale}
              onReason={setCustomerReason}
            />
          )}

          {tab === 'slot' && (
            <SlotForm
              partySize={partySize}
              originalPartySize={booking.party_size}
              date={date}
              slots={slots}
              slotsLoading={slotsLoading}
              slotsError={slotsError}
              pickedSlot={pickedSlot}
              isTimeSplit={isTimeSplit}
              isMultiPick={isMultiPick}
              experiencePicks={booking.experience_picks}
              isPaid={booking.payment_status === 'paid'}
              isArrived={booking.lifecycle_status === 'arrived'}
              isOverCapacity={isOverCapacity}
              remainingCapacity={remainingCapacity}
              overflow={overflow}
              splitNext={splitNext}
              canSplit={canSplit}
              overCapacityChoice={overCapacityChoice}
              reason={slotReason}
              singleRoomLocations={singleRoomLocations}
              existingSlotKey={existingSlotKey}
              onPartySize={setPartySize}
              onDate={setDate}
              onSlot={setPickedSlot}
              onOverCapacityChoice={setOverCapacityChoice}
              onReason={setSlotReason}
            />
          )}

          {tab === 'reduce' && (
            isSingleSlot ? (
              <ReduceForm
                eligible={reduceEligible}
                isMultiPick={isMultiPick}
                isSingleSlot={isSingleSlot}
                isPaid={isPaid}
                currentParty={booking.party_size}
                newParty={reduceNewParty}
                totalCents={reduceTotalCents}
                newTotalCents={reduceNewTotalCents}
                creditCents={reduceCreditCents}
                isArrived={booking.lifecycle_status === 'arrived'}
                reason={reduceReason}
                onNewParty={setReduceNewParty}
                onReason={setReduceReason}
              />
            ) : (
              <ReduceSlotForm
                eligible={reduceEligible}
                isArrived={booking.lifecycle_status === 'arrived'}
                isMultiPick={isMultiPick}
                slots={booking.booking_slots}
                picks={booking.experience_picks}
                selectedSlotId={reduceSlotId}
                slotNewParty={reduceSlotNewParty}
                slotMin={reduceSlotMin}
                creditCents={reduceSlotCreditCents}
                newBookingTotalCents={reduceSlotNewBookingTotalCents}
                reason={reduceReason}
                onSelectSlot={selectReduceSlot}
                onSlotNewParty={setReduceSlotNewParty}
                onReason={setReduceReason}
              />
            )
          )}

          {submitError ? (
            <div className="error-card walkin-submit-error">{submitError}</div>
          ) : null}

          <div className="walkin-step-actions">
            <button
              className="btn-link"
              disabled={submitting}
              onClick={() => onClose(false)}
            >
              Annuleren
            </button>
            <button
              className="walkin-primary-btn"
              disabled={
                submitting ||
                (tab === 'customer'
                  ? !customerValid || !customerDirty
                  : tab === 'slot'
                    ? !slotValid
                    : !reduceCanSubmit)
              }
              onClick={() => {
                if (tab === 'customer') void submitCustomer();
                else if (tab === 'slot') void submitSlot();
                else void submitReduce();
              }}
            >
              {submitting
                ? 'Bezig…'
                : tab === 'reduce'
                  ? 'Verkleinen + tegoedbon'
                  : 'Opslaan'}
            </button>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function CustomerForm({
  firstName, lastName, email, phone, locale, reason,
  onFirstName, onLastName, onEmail, onPhone, onLocale, onReason,
}: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  locale: string;
  reason: string;
  onFirstName: (v: string) => void;
  onLastName: (v: string) => void;
  onEmail: (v: string) => void;
  onPhone: (v: string) => void;
  onLocale: (v: string) => void;
  onReason: (v: string) => void;
}) {
  return (
    <div className="walkin-step">
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Voornaam</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => onFirstName(e.target.value)}
          />
        </label>
        <label className="walkin-field">
          <span>Achternaam</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => onLastName(e.target.value)}
          />
        </label>
      </div>

      <label className="walkin-field">
        <span>Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          autoComplete="off"
        />
      </label>

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Telefoon</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => onPhone(e.target.value)}
          />
        </label>
        <label className="walkin-field">
          <span>Taal</span>
          <select value={locale} onChange={(e) => onLocale(e.target.value)}>
            {LOCALES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="walkin-field">
        <span>Reden (optioneel, wordt gelogd)</span>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => onReason(e.target.value)}
        />
      </label>

      <p className="muted walkin-hint">
        Wijzigingen gelden voor elke boeking met dit klantenrecord.
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------
function SlotForm({
  partySize, originalPartySize, date, slots, slotsLoading, slotsError,
  pickedSlot, isTimeSplit, isMultiPick, experiencePicks,
  isPaid, isArrived, isOverCapacity, remainingCapacity,
  overflow, splitNext, canSplit, overCapacityChoice,
  reason, singleRoomLocations, existingSlotKey,
  onPartySize, onDate, onSlot, onOverCapacityChoice, onReason,
}: {
  partySize: number;
  originalPartySize: number;
  date: string;
  slots: Departure[];
  slotsLoading: boolean;
  slotsError: string | null;
  pickedSlot: Departure | null;
  isTimeSplit: boolean;
  isMultiPick: boolean;
  experiencePicks: ExperiencePickRow[];
  isPaid: boolean;
  isArrived: boolean;
  isOverCapacity: boolean;
  remainingCapacity: number;
  overflow: number;
  splitNext: Departure | null;
  canSplit: boolean;
  overCapacityChoice: 'force' | 'split';
  reason: string;
  singleRoomLocations: Set<string>;
  existingSlotKey: string | null;
  onPartySize: (v: number) => void;
  onDate: (v: string) => void;
  onSlot: (v: Departure) => void;
  onOverCapacityChoice: (v: 'force' | 'split') => void;
  onReason: (v: string) => void;
}) {
  const partyChanged = partySize !== originalPartySize;
  const showPriceWarning = partyChanged && isPaid;

  // Auto-center the auto-picked slot in the scrollbox the first time the
  // list mounts with a picked slot. Uses getBoundingClientRect so the calc
  // is independent of where `position: relative` ancestors sit — `offsetTop`
  // alone gave wrong offsets because `.walkin-slot-list` isn't a positioned
  // ancestor, so the picked button was offset from the modal body instead.
  const listRef = useRef<HTMLUListElement | null>(null);
  const hasCenteredRef = useRef(false);
  useEffect(() => {
    if (hasCenteredRef.current) return;
    if (!pickedSlot || slots.length === 0) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>('.walkin-slot-picked');
    if (!el) return;
    const listRect = list.getBoundingClientRect();
    const elRect   = el.getBoundingClientRect();
    const offsetWithinList = (elRect.top - listRect.top) + list.scrollTop;
    const target = offsetWithinList - (list.clientHeight - el.clientHeight) / 2;
    const max = list.scrollHeight - list.clientHeight;
    list.scrollTop = Math.max(0, Math.min(target, max));
    hasCenteredRef.current = true;
  }, [pickedSlot, slots.length]);

  return (
    <div className="walkin-step">
      {isArrived ? (
        <div className="cancel-warn">
          <strong>Let op:</strong> deze klant is al ingecheckt. Het verplaatsen
          herschrijft de slots; de boekingstatus blijft <em>arrived</em>.
          Scan de QR opnieuw bij het nieuwe slot om de check-in op het juiste
          slot vast te leggen.
        </div>
      ) : null}
      {isMultiPick ? (
        <div className="cancel-warn">
          Deze boeking heeft meerdere expedities op één tijdslot. Verplaatsen
          schuift <strong>alle expedities samen</strong> naar het gekozen
          tijdslot; de kamer en het aantal personen per expeditie blijven
          gelijk.
        </div>
      ) : isTimeSplit ? (
        <div className="cancel-warn">
          Deze boeking is gesplitst over meerdere tijdsloten. Single-slot
          bewerken in de admin is v1; voor split-bewerking gebruik de
          klant-manage-link.
        </div>
      ) : null}

      {isMultiPick ? (
        <div className="walkin-field">
          <span>Expedities</span>
          <ul className="walkin-slot-list edit-pick-list">
            {experiencePicks.map((p) => (
              <li key={p.id} className="edit-pick-row">
                <strong>{p.experiences?.name ?? 'Expeditie'}</strong>
                <span className="muted">{p.party_size}p</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Aantal personen</span>
          <input
            type="number"
            min={1}
            max={30}
            value={partySize}
            disabled={isMultiPick}
            title={isMultiPick ? 'Bij een multi-expeditie boeking blijft het aantal gelijk.' : undefined}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) {
                onPartySize(Math.max(1, Math.min(30, Math.floor(n))));
              }
            }}
          />
        </label>
        <label className="walkin-field">
          <span>Datum</span>
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && onDate(e.target.value)}
          />
        </label>
      </div>

      {showPriceWarning ? (
        <div className="cancel-warn">
          Aantal gewijzigd ({originalPartySize} → {partySize}). Het totaalbedrag
          wordt <strong>niet automatisch aangepast</strong>. Voor
          terugbetalen of bijbetalen gebruik annuleer/walk-in.
        </div>
      ) : null}

      <div className="walkin-slots">
        <div className="walkin-section-head">Tijdslot</div>
        {slotsLoading ? (
          <div className="muted">Slots ophalen…</div>
        ) : slotsError ? (
          <div className="error-card">{slotsError}</div>
        ) : slots.length === 0 ? (
          <div className="muted">Geen slots op deze datum.</div>
        ) : (
          <ul className="walkin-slot-list" ref={listRef}>
            {slots.map((s) => {
              const remaining = Math.max(0, s.capacity - s.booked);
              const full = remaining <= 0;
              const isPicked =
                pickedSlot != null &&
                pickedSlot.room_id === s.room_id &&
                pickedSlot.slot_start === s.slot_start;
              const isCurrent =
                existingSlotKey != null &&
                `${s.room_id}|${s.slot_start}` === existingSlotKey;
              const showRoom = !singleRoomLocations.has(s.location_id);
              return (
                <li key={`${s.room_id}-${s.slot_start}`}>
                  <button
                    type="button"
                    className={`walkin-slot ${
                      isPicked ? 'walkin-slot-picked' : ''
                    } ${full ? 'walkin-slot-full' : ''}`}
                    onClick={() => onSlot(s)}
                  >
                    <span className="walkin-slot-time">
                      {s.time_of_day}
                      {isCurrent ? (
                        <span className="walkin-slot-current-badge">Huidig</span>
                      ) : null}
                    </span>
                    {showRoom ? (
                      <span className="walkin-slot-room muted">
                        {s.room_name}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span
                      className={`walkin-slot-cap ${full ? 'walkin-slot-cap-full' : ''}`}
                    >
                      {s.booked} / {s.capacity}
                      {full ? ' · vol' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {isOverCapacity ? (
        <div className="walkin-warning">
          <strong>Slot is vol.</strong>{' '}
          Nog {remainingCapacity} vrij, jij wilt er {partySize} plaatsen ({overflow} te veel).
          <fieldset className="cancel-refund-options edit-overcap-options">
            <legend>Wat wil je doen?</legend>

            {/* Split-to-next-slot is single-pick only; multi-pick moves as a
                unit, so the only escape is force. */}
            {!isMultiPick ? (
              canSplit && splitNext ? (
                <label className="cancel-radio">
                  <input
                    type="radio"
                    checked={overCapacityChoice === 'split'}
                    onChange={() => onOverCapacityChoice('split')}
                  />
                  <span>
                    Splits naar <strong>{splitNext.time_of_day}</strong>
                    {' '}({remainingCapacity}p hier + {overflow}p daar)
                  </span>
                </label>
              ) : (
                <p className="muted edit-overcap-hint">
                  {splitNext
                    ? `Splitsen kan niet — ${splitNext.time_of_day} heeft niet genoeg ruimte voor ${overflow}p.`
                    : 'Geen volgend tijdslot om naar te splitsen op deze dag.'}
                </p>
              )
            ) : null}

            <label className="cancel-radio">
              <input
                type="radio"
                checked={overCapacityChoice === 'force'}
                onChange={() => onOverCapacityChoice('force')}
              />
              <span>Boek toch over capaciteit heen ({partySize}p in dit slot)</span>
            </label>
          </fieldset>
        </div>
      ) : null}

      <label className="walkin-field">
        <span>Reden (optioneel, wordt gelogd)</span>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => onReason(e.target.value)}
        />
      </label>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ReduceForm — shrink a single-pick / single-slot paid departure's party size
// and issue a tegoedbon for the freed seats. Renders a hint when the booking
// shape isn't eligible (multi-pick, time-split, unpaid, or already 1 person).
// -----------------------------------------------------------------------------
function ReduceForm({
  eligible, isMultiPick, isSingleSlot, isPaid, currentParty, newParty,
  totalCents, newTotalCents, creditCents, isArrived, reason,
  onNewParty, onReason,
}: {
  eligible: boolean;
  isMultiPick: boolean;
  isSingleSlot: boolean;
  isPaid: boolean;
  currentParty: number;
  newParty: number;
  totalCents: number;
  newTotalCents: number;
  creditCents: number;
  isArrived: boolean;
  reason: string;
  onNewParty: (v: number) => void;
  onReason: (v: string) => void;
}) {
  const eur = (c: number) => `€ ${(c / 100).toFixed(2)}`;
  const removed = currentParty - newParty;
  const seatWord = removed === 1 ? 'plaats' : 'plaatsen';

  if (!eligible) {
    let why = 'Verkleinen met tegoedbon is voor deze boeking niet beschikbaar.';
    if (!isPaid) {
      why = 'Alleen betaalde boekingen kunnen met een tegoedbon worden verkleind.';
    } else if (isMultiPick) {
      why =
        'Verkleinen is niet beschikbaar voor boekingen met meerdere expedities op één vertrek.';
    } else if (!isSingleSlot) {
      why =
        'Verkleinen is niet beschikbaar voor boekingen die over meerdere tijdsloten zijn gesplitst.';
    } else if (currentParty < 2) {
      why =
        'Deze boeking heeft maar 1 persoon en kan niet verder worden verkleind — gebruik Annuleren.';
    }
    return (
      <div className="walkin-step">
        <div className="cancel-warn">{why}</div>
      </div>
    );
  }

  return (
    <div className="walkin-step">
      {isArrived ? (
        <div className="cancel-warn">
          <strong>Let op:</strong> deze klant is al ingecheckt. Verkleinen past het
          aantal personen en het bedrag aan; de boekingstatus blijft <em>arrived</em>.
        </div>
      ) : null}

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Nieuw aantal personen</span>
          <input
            type="number"
            min={1}
            max={currentParty - 1}
            value={newParty}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) {
                onNewParty(Math.max(1, Math.min(currentParty - 1, Math.floor(n))));
              }
            }}
          />
        </label>
        <label className="walkin-field">
          <span>Huidig</span>
          <input type="text" value={`${currentParty}p · ${eur(totalCents)}`} disabled />
        </label>
      </div>

      <fieldset className="cancel-refund-options reduce-summary">
        <legend>Na verkleinen</legend>
        <div className="reduce-summary-row">
          <span className="muted">Nieuw aantal</span>
          <strong>{newParty}p</strong>
        </div>
        <div className="reduce-summary-row">
          <span className="muted">Nieuw bedrag</span>
          <strong>{eur(newTotalCents)}</strong>
        </div>
        <div className="reduce-summary-row">
          <span className="muted">Tegoedbon ({removed} {seatWord})</span>
          <strong>{eur(creditCents)}</strong>
        </div>
      </fieldset>

      <p className="muted cancel-method-hint">
        De {removed} vrijgekomen {seatWord} {removed === 1 ? 'wordt' : 'worden'} weer
        beschikbaar voor andere boekingen. Er wordt een tegoedbon van {eur(creditCents)}{' '}
        per e-mail naar de klant gestuurd; de betaling wordt niet teruggestort.
      </p>

      <label className="walkin-field">
        <span>Reden (optioneel, wordt gelogd)</span>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => onReason(e.target.value)}
        />
      </label>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ReduceSlotForm — reduce ONE timeslot of a time-split or multi-pick departure.
// The operator picks a slot row (experience · time · room · current Np) and
// lowers its count; the tegoedbon is priced from that slot's pick. Setting a
// leg to 0 removes it (only offered when the pick has 2+ legs).
// -----------------------------------------------------------------------------
function ReduceSlotForm({
  eligible, isArrived, isMultiPick, slots, picks, selectedSlotId,
  slotNewParty, slotMin, creditCents, newBookingTotalCents, reason,
  onSelectSlot, onSlotNewParty, onReason,
}: {
  eligible: boolean;
  isArrived: boolean;
  isMultiPick: boolean;
  slots: BookingSlotRow[];
  picks: ExperiencePickRow[];
  selectedSlotId: string | null;
  slotNewParty: number;
  slotMin: number;
  creditCents: number;
  newBookingTotalCents: number;
  reason: string;
  onSelectSlot: (slotId: string) => void;
  onSlotNewParty: (v: number) => void;
  onReason: (v: string) => void;
}) {
  const eur = (c: number) => `€ ${(c / 100).toFixed(2)}`;
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('nl-NL', {
      timeZone: 'Europe/Amsterdam',
      hour: '2-digit',
      minute: '2-digit',
    });
  const pickName = (depExpId?: string) =>
    picks.find((p) => p.id === depExpId)?.experiences?.name ?? 'Expeditie';

  if (!eligible) {
    return (
      <div className="walkin-step">
        <div className="cancel-warn">
          Verkleinen met tegoedbon is voor deze boeking niet beschikbaar.
        </div>
      </div>
    );
  }

  const selected = slots.find((s) => s.id === selectedSlotId) ?? null;
  const removed = selected ? selected.party_size - slotNewParty : 0;
  const seatWord = removed === 1 ? 'plaats' : 'plaatsen';
  const willRemoveLeg = slotMin === 0 && slotNewParty === 0;

  return (
    <div className="walkin-step">
      {isArrived ? (
        <div className="cancel-warn">
          <strong>Let op:</strong> deze klant is al ingecheckt. Verkleinen past het
          aantal personen en het bedrag aan; de boekingstatus blijft <em>arrived</em>.
        </div>
      ) : null}

      <div className="walkin-field">
        <span>Kies het tijdslot om te verkleinen</span>
        <ul className="walkin-slot-list">
          {slots.map((s) => {
            const isPicked = s.id === selectedSlotId;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={`walkin-slot ${isPicked ? 'walkin-slot-picked' : ''}`}
                  onClick={() => onSelectSlot(s.id)}
                >
                  <span className="walkin-slot-time">
                    {fmtTime(s.slot_start)}
                    {isMultiPick ? (
                      <span className="walkin-slot-room muted">
                        {' · '}{pickName(s.departure_experience_id)}
                      </span>
                    ) : null}
                  </span>
                  <span className="walkin-slot-room muted">{s.rooms?.name ?? ''}</span>
                  <span className="walkin-slot-cap">{s.party_size}p</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {selected ? (
        <>
          <div className="walkin-row">
            <label className="walkin-field">
              <span>Nieuw aantal op dit tijdslot</span>
              <input
                type="number"
                min={slotMin}
                max={selected.party_size - 1}
                value={slotNewParty}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) {
                    onSlotNewParty(
                      Math.max(slotMin, Math.min(selected.party_size - 1, Math.floor(n))),
                    );
                  }
                }}
              />
            </label>
            <label className="walkin-field">
              <span>Huidig op dit tijdslot</span>
              <input type="text" value={`${selected.party_size}p`} disabled />
            </label>
          </div>

          <fieldset className="cancel-refund-options reduce-summary">
            <legend>Na verkleinen</legend>
            <div className="reduce-summary-row">
              <span className="muted">Verwijderd</span>
              <strong>{removed} {seatWord}</strong>
            </div>
            <div className="reduce-summary-row">
              <span className="muted">Nieuw bedrag</span>
              <strong>{eur(newBookingTotalCents)}</strong>
            </div>
            <div className="reduce-summary-row">
              <span className="muted">Tegoedbon ({removed} {seatWord})</span>
              <strong>{eur(creditCents)}</strong>
            </div>
          </fieldset>

          <p className="muted cancel-method-hint">
            {willRemoveLeg ? (
              <>
                Dit tijdslot wordt volledig verwijderd; de overige tijdsloten van
                deze expeditie blijven staan.{' '}
              </>
            ) : null}
            De {removed} vrijgekomen {seatWord} {removed === 1 ? 'wordt' : 'worden'} weer
            beschikbaar voor andere boekingen. Er wordt een tegoedbon van {eur(creditCents)}{' '}
            per e-mail naar de klant gestuurd; de betaling wordt niet teruggestort.
          </p>
        </>
      ) : null}

      <label className="walkin-field">
        <span>Reden (optioneel, wordt gelogd)</span>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => onReason(e.target.value)}
        />
      </label>
    </div>
  );
}
